import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, useNavigate, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { AppProvider } from "@/context/AppContext";
import { api } from "@/lib/api";
import Login from "@/pages/Login";
import Layout from "@/components/Layout";
import Dashboard from "@/pages/Dashboard";
import Goals from "@/pages/Goals";
import PatientJourney from "@/pages/PatientJourney";
import FollowUps from "@/pages/FollowUps";
import Feedback from "@/pages/Feedback";
import Concerns from "@/pages/Concerns";
import Dues from "@/pages/Dues";
import Calls from "@/pages/Calls";
import Referrals from "@/pages/Referrals";
import Testimonials from "@/pages/Testimonials";
import MemberGoals from "@/pages/MemberGoals";
import Upsells from "@/pages/Upsells";
import Complications from "@/pages/Complications";
import Pricing from "@/pages/Pricing";
import LookFeel from "@/pages/LookFeel";
import Team from "@/pages/Team";
import Settings from "@/pages/Settings";

const Loader = () => (
  <div className="min-h-screen flex items-center justify-center bg-[#0F172A]">
    <div className="flex flex-col items-center gap-3">
      <div className="h-10 w-10 rounded-full border-2 border-[#C9A24B] border-t-transparent animate-spin" />
      <p className="text-slate-400 text-sm tracking-widest uppercase">Loading</p>
    </div>
  </div>
);

const AuthCallback = () => {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  useEffect(() => {
    const hash = window.location.hash;
    const match = hash.match(/session_id=([^&]+)/);
    const process = async () => {
      if (!match) { navigate("/login"); return; }
      try {
        const res = await api.post("/auth/session", { session_id: match[1] });
        setUser(res.data);
        window.history.replaceState(null, "", "/dashboard");
        navigate("/dashboard", { replace: true });
      } catch {
        navigate("/login", { replace: true });
      }
    };
    process();
    // eslint-disable-next-line
  }, []);
  return <Loader />;
};

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <Loader />;
  if (!user) return <Navigate to="/login" replace />;
  return <AppProvider><Layout>{children}</Layout></AppProvider>;
};

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/goals" element={<ProtectedRoute><Goals /></ProtectedRoute>} />
      <Route path="/journey" element={<ProtectedRoute><PatientJourney /></ProtectedRoute>} />
      <Route path="/followups" element={<ProtectedRoute><FollowUps /></ProtectedRoute>} />
      <Route path="/feedback" element={<ProtectedRoute><Feedback /></ProtectedRoute>} />
      <Route path="/concerns" element={<ProtectedRoute><Concerns /></ProtectedRoute>} />
      <Route path="/dues" element={<ProtectedRoute><Dues /></ProtectedRoute>} />
      <Route path="/calls" element={<ProtectedRoute><Calls /></ProtectedRoute>} />
      <Route path="/referrals" element={<ProtectedRoute><Referrals /></ProtectedRoute>} />
      <Route path="/testimonials" element={<ProtectedRoute><Testimonials /></ProtectedRoute>} />
      <Route path="/team-goals" element={<ProtectedRoute><MemberGoals /></ProtectedRoute>} />
      <Route path="/upsells" element={<ProtectedRoute><Upsells /></ProtectedRoute>} />
      <Route path="/complications" element={<ProtectedRoute><Complications /></ProtectedRoute>} />
      <Route path="/pricing" element={<ProtectedRoute><Pricing /></ProtectedRoute>} />
      <Route path="/lookfeel" element={<ProtectedRoute><LookFeel /></ProtectedRoute>} />
      <Route path="/team" element={<ProtectedRoute><Team /></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <div className="noise-overlay" />
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
        </AuthProvider>
      </BrowserRouter>
      <Toaster position="top-right" theme="dark" />
    </div>
  );
}

export default App;
